import React, { useState, useEffect, useRef } from "react";
import { Box, Card, CircularProgress, Grid, IconButton, Skeleton, TextField } from "@mui/material";
import ArticuloItem from "./ArticuloItem";

import DialogImg from "./DialogImg";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import catalogo from "../api/catalogo";
import { SkeletonArticulosList } from "./SkeletonArticulosList";

export default function ArticulosListContainer({ grupoId, onComandaChange, dynamicColor }) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [articulos, setArticulos] = useState([]);
  const [cantidades, setCantidades] = useState({});
  const [img, setImg] = useState("");
  const [desc, setDesc] = useState("");
  const [openImg, setOpenImg] = useState(false);
  const [observaciones, setObservaciones] = useState({});
  const [pressedIndex, setPressedIndex] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setPageNumber(1);
    setHasNextPage(false);
    setTotalPages(0);
    setArticulos([]);

    const comanda = JSON.parse(localStorage.getItem("comanda") || "{}");
    const detalles = comanda.comandaDetalles || [];
    const map = {};
    const obsMap = {};
    detalles.forEach(d => {
      if (d.idArticulo) {
        map[d.idArticulo] = d.cantidadArticulo;
        obsMap[d.idArticulo] = d.descripcionPersonalizada || d.comentarios || "";
      }
    });
    setCantidades(map);
    setObservaciones(obsMap);

    const fetchArticulos = async () => {
      try {
        setLoading(true);
        const response = await catalogo.getArticulosItems(1, 10, "", grupoId);
        setArticulos(response.items);
        setPageNumber(response.pageNumber);
        setHasNextPage(response.hasNextPage);
        setTotalPages(response.totalPages);
        console.log("Artículos obtenidos:", response);
      } catch (error) {
        console.error("Error fetching articulos:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticulos();
  }, [grupoId]);

  // Efecto para notificar cambios en la comanda
  useEffect(() => {
    if (onComandaChange) {
      onComandaChange();
    }
  }, [cantidades, onComandaChange]);

  const loadMoreArticulos = async () => {
    if (loadingMore || !hasNextPage || pageNumber >= totalPages) return;

    try {
      setLoadingMore(true);
      const nextPage = pageNumber + 1;
      const response = await catalogo.getArticulosItems(nextPage, 10, "", grupoId);

      setArticulos(prev => [...prev, ...response.items]);
      setPageNumber(response.pageNumber);
      setHasNextPage(response.hasNextPage);
      setTotalPages(response.totalPages);
      console.log("Más artículos obtenidos:", response);
    } catch (error) {
      console.error("Error fetching more articulos:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleScroll = e => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 5) {
      loadMoreArticulos();
    }
  };

  const handleClickImg = (imgSrc, descripcion) => {
    setImg(imgSrc);
    setDesc(descripcion);
    setOpenImg(true);
  };

  const handleCloseImg = () => {
    setImg("");
    setOpenImg(false);
  };

  const handleClickEditar = index => {
    setPressedIndex(prev => (prev === index ? null : index));
  };

  const handleIncrease = (articulo, nuevaCantidad) => {
    // Si no se pasa nuevaCantidad, calcularla
    const cantidad = nuevaCantidad || (cantidades[articulo.idArticulo] || 0) + 1;

    setCantidades(prev => {
      let comanda = JSON.parse(localStorage.getItem("comanda") || "{}");
      if (!comanda.comandaDetalles) comanda = { comandaDetalles: [], importeFinal: 0 };
      let detalles = comanda.comandaDetalles;
      const idx = detalles.findIndex(d => d.idArticulo === articulo.idArticulo);

      if (idx === -1 && cantidad === 1) {
        detalles.push({
          ...articulo,
          precioUnitario: articulo.precioPublico,
          cantidadArticulo: cantidad,
          comandaDetalleElementos: [],
        });
      } else if (idx !== -1) {
        detalles[idx].cantidadArticulo = cantidad;
      }

      comanda.comandaDetalles = detalles;
      comanda.importeFinal = detalles.reduce((acc, d) => acc + d.precioUnitario * d.cantidadArticulo, 0);
      localStorage.setItem("comanda", JSON.stringify(comanda));

      return { ...prev, [articulo.idArticulo]: cantidad };
    });
  };

  const handleDecrease = (articulo, isClean, nuevaCantidad) => {
    setCantidades(prev => {
      const actual = prev[articulo.idArticulo] || 0;

      // Si es isClean, eliminar completamente el artículo
      if (isClean) {
        let comanda = JSON.parse(localStorage.getItem("comanda") || "{}");
        if (!comanda.comandaDetalles) return prev;
        let detalles = comanda.comandaDetalles;
        const idx = detalles.findIndex(d => d.idArticulo === articulo.idArticulo);
        if (idx !== -1) {
          detalles.splice(idx, 1);
        }
        comanda.comandaDetalles = detalles;
        comanda.importeFinal = detalles.reduce((acc, d) => acc + d.precioUnitario * d.cantidadArticulo, 0);
        localStorage.setItem("comanda", JSON.stringify(comanda));

        return { ...prev, [articulo.idArticulo]: 0 };
      }

      // Usar nuevaCantidad si se proporciona, sino usar actual - 1
      const cantidad = nuevaCantidad !== undefined ? nuevaCantidad : actual - 1;

      if (cantidad < 0) return prev;

      let comanda = JSON.parse(localStorage.getItem("comanda") || "{}");
      if (!comanda.comandaDetalles) return prev;
      let detalles = comanda.comandaDetalles;
      const idx = detalles.findIndex(d => d.idArticulo === articulo.idArticulo || d.idProducto === articulo.idProducto);

      if (idx !== -1) {
        if (cantidad === 0) {
          detalles.splice(idx, 1);
        } else {
          detalles[idx].cantidadArticulo = cantidad;
        }
      }

      comanda.comandaDetalles = detalles;
      comanda.importeFinal = detalles.reduce((acc, d) => acc + d.precioUnitario * d.cantidadArticulo, 0);
      localStorage.setItem("comanda", JSON.stringify(comanda));

      return { ...prev, [articulo.idArticulo]: cantidad };
    });
  };

  const handleObservacionChange = (idArticulo, value) => {
    setObservaciones(prev => ({ ...prev, [idArticulo]: value }));
    // Actualiza en localStorage
    let comanda = JSON.parse(localStorage.getItem("comanda") || "{}");
    if (!comanda.comandaDetalles) return;
    const idx = comanda.comandaDetalles.findIndex(d => d.idArticulo === idArticulo);
    if (idx !== -1) {
      comanda.comandaDetalles[idx].comentarios = value;
      comanda.comandaDetalles[idx].descripcionPersonalizada = value; // También guardar como descripcionPersonalizada
      localStorage.setItem("comanda", JSON.stringify(comanda));
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
      }}
      onScroll={handleScroll}
    >
      {loading ? (
        <SkeletonArticulosList />
      ) : (
        <>
          {articulos &&
            articulos.map((item, idx) => (
              <Box key={`${item.idProducto}-${idx}`}>
                <ArticuloItem
                  key={item.idArticulo}
                  item={{
                    ...item,
                    cantidadArticulo: cantidades[item.idArticulo] || 0,
                    precioUnitario: item.precioPublico,
                    comandaDetalleElementos: [],
                  }}
                  itemProducto={item}
                  isBarcode={false}
                  tieneObs={observaciones[item.idArticulo] && observaciones[item.idArticulo].length > 0}
                  index={idx}
                  onClickImg={imgSrc => handleClickImg(imgSrc, item.descripcion)}
                  onIncrease={nuevaCantidad => handleIncrease(item, nuevaCantidad)}
                  onDecrease={nuevaCantidad => handleDecrease(item, false, nuevaCantidad)}
                  onEdit={() => handleClickEditar(idx)}
                  onDelete={() => handleDecrease(item, true)}
                  dynamicColor={dynamicColor}
                />
                {pressedIndex === idx && (
                  <Box sx={{ p: 1 }} ref={containerRef}>
                    <Card sx={{ mt: -1.5, p: 1, mb: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Observación"
                          variant="outlined"
                          onFocus={() => {
                            containerRef.current?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          }}
                          value={observaciones[item.idArticulo] || ""}
                          onChange={e => handleObservacionChange(item.idArticulo, e.target.value)}
                          InputLabelProps={{
                            style: { fontSize: "0.85rem", color: "gray", width: "80%" },
                          }}
                        />
                        <IconButton
                          onClick={() => setPressedIndex(null)}
                          sx={{
                            color: "#514641",
                          }}
                        >
                          <HighlightOffOutlinedIcon sx={{ fontSize: 14, mr: -0.4 }} />
                        </IconButton>
                      </Box>
                    </Card>
                  </Box>
                )}
              </Box>
            ))}
          {loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </>
      )}
      <DialogImg imgSrc={img} desc={desc} open={openImg} onClose={() => handleCloseImg()} />
    </Box>
  );
}
